// Plate maths for the sleeve and the ramp. Pure functions, no Vue.
// Weights are in the kit's unit; maths runs in hundredths so 1.25 + 0.25 never drifts.
const U = 100
const toU = (w) => Math.round(w * U)
const fromU = (u) => u / U
const sum = (xs) => xs.reduce((a, b) => a + b, 0)
const WARM_MIN = 2.5 // anything lighter is a change plate: on only where a total needs it

// The last warm-up jump: from the final warm-up to the top set, in the kit's unit.
// 10kg and 20lb are the usual gap before a heavy single. The step is one pair of
// the smallest plate a gym can be counted on to own, and the ceiling is a jump
// nobody means, so a thumb held on + cannot quietly empty the ramp.
// The step is also the kit's increment: what every warm-up is rounded to.
const JUMPS = { kg: { usual: 10, step: 2.5, max: 50 }, lb: { usual: 20, step: 5, max: 100 } }
export const jumpRule = (unit) => JUMPS[unit] ?? JUMPS.kg
export const clampJump = (w, unit) => Math.min(jumpRule(unit).max, Math.max(jumpRule(unit).step, w))
// A lift saved before the jump existed has no `lastJump`, and reads as the usual one.
export const savedJump = (lift, unit) => clampJump(Number(lift?.lastJump) || jumpRule(unit).usual, unit)

export const fmt = (w) => String(+w.toFixed(2))
// AN EMPTY BAR HAS NO COLLARS. Collars go on with the first plate and come off
// with the last, so the bar alone is `bare` (20) and anything with plates on is
// `base` (bar and both collars, 25) plus the plates.
export const bare = (kit) => toU(kit.barWeight)
export const base = (kit) => toU(kit.barWeight) + 2 * toU(kit.collarWeight || 0)
export const countBar = (side, kit) => fromU(side.length ? base(kit) + 2 * sum(side.map(toU)) : bare(kit))

// Reps are a count or a range. `reps` is the low end; `repsTo` is the top of the
// range only when it is higher, so a half-typed or backwards one reads as no range.
export const rangeTo = (reps, to) => (Number(to) > Number(reps) ? Number(to) : null)
export function repsText(reps, to) {
  const n = Number(reps) || 0
  const t = rangeTo(n, to)
  return t ? `${n}–${t} reps` : `${n} ${n === 1 ? 'rep' : 'reps'}`
}

// A scheme reduced to what loads a bar, so two that would load the same ramp
// compare equal and a stored one carries nothing a screen invented.
export function tidyScheme(s) {
  return {
    startWeight: Number(s?.startWeight) || 0,
    startReps: Number(s?.startReps) || 5,
    startRepsTo: rangeTo(Number(s?.startReps) || 5, s?.startRepsTo),
    steps: (s?.steps ?? []).map((x) => ({
      percent: Number(x.percent) || 0,
      of: x.of === 'last' ? 'last' : 'top',
      reps: Number(x.reps) || 1,
      repsTo: rangeTo(Number(x.reps) || 1, x.repsTo),
    })),
  }
}

function sizes(plates, minWeight = 0) {
  return plates
    .filter((p) => p.pairs > 0 && p.weight >= minWeight)
    .map((p) => ({ u: toU(p.weight), pairs: p.pairs }))
    .sort((a, b) => b.u - a.u)
}

// Every heaviest-inside stack for ONE side whose sum lands in [lo, hi], fewest plates first.
function stacks(plates, lo, hi, { minWeight = 0, cap = 300, maxPlates = 10 } = {}) {
  const s = sizes(plates, minWeight)
  const room = s.map((_, i) => sum(s.slice(i).map((x) => x.u * x.pairs)))
  const out = []
  const walk = (i, total, stack) => {
    if (out.length >= cap || total + (room[i] || 0) < lo) return
    if (i === s.length || stack.length === maxPlates) {
      if (total >= lo && total <= hi) out.push(stack.slice())
      return
    }
    const { u, pairs } = s[i]
    for (let c = Math.min(pairs, Math.floor((hi - total) / u), maxPlates - stack.length); c >= 0; c--) {
      for (let k = 0; k < c; k++) stack.push(u)
      walk(i + 1, total + c * u, stack)
      stack.length -= c
    }
  }
  walk(0, 0, [])
  return out.sort((a, b) => a.length - b.length)
}

function reachable(plates) {
  let sums = new Set([0])
  for (const { u, pairs } of sizes(plates)) {
    const next = new Set(sums)
    for (const x of sums) for (let c = 1; c <= pairs; c++) next.add(x + c * u)
    sums = next
  }
  return [...sums].sort((a, b) => a - b)
}

// Target total -> plates per side, or the nearest totals this kit can actually make.
// The bar alone is its own answer; every other total has collars on, so
// bar-and-collars with nothing between them (25) is not a loading anyone does.
export function loadBar(target, kit, plates) {
  const b = base(kit)
  const t = toU(target)
  if (t === bare(kit)) return { ok: true, total: target, stack: [] }
  if (t < bare(kit)) return { ok: false, reason: 'under-bar', below: null, above: fromU(bare(kit)) }
  const side = (t - b) / 2
  const exact = side > 0 && Number.isInteger(side) ? stacks(plates, side, side, { cap: 50 }) : []
  if (exact.length) return { ok: true, total: target, stack: exact[0].map(fromU) }
  const sums = reachable(plates).filter((x) => x > 0)
  const below = sums.filter((x) => x < side).pop()
  const above = sums.find((x) => x > side)
  return {
    ok: false,
    reason: 'not-loadable',
    below: below == null ? fromU(bare(kit)) : fromU(b + 2 * below),
    above: above == null ? null : fromU(b + 2 * above),
  }
}

// Only as many change plates as the total needs. They are fiddly and they are
// what a lifter runs out of, and a stack built on them to save one strip is a
// top set with nine plates a side.
const small = (stack) => stack.filter((u) => u < toU(WARM_MIN)).length
const fewestChange = (list) => {
  const least = Math.min(...list.map(small))
  return list.filter((s) => small(s) === least)
}

// Every sensible way one total goes on the bar: warm-up plates alone when they
// can make it, otherwise the fewest change plates — and of those, only the
// stacks with the FEWEST PLATES. Keeping plates on between sets is a tiebreak,
// not a licence to pile up small ones: without this, 180 went on as
// 25/20/10/10/5/5/2.5 a side because each set had grown out of the last, where
// any lifter loads 25/25/25/2.5.
function loadings(total, b, plates, bareU) {
  if (total === bareU) return [[]]
  const side = (total - b) / 2
  const warm = stacks(plates, side, side, { minWeight: WARM_MIN })
  const all = warm.length ? warm : fewestChange(stacks(plates, side, side, { cap: 50 }))
  const least = Math.min(...all.map((s) => s.length))
  return all.filter((s) => s.length === least)
}

// The total this kit makes nearest `t`: `t` itself when it can, else the nearest
// either side — both when they are equally near, and the plate changes pick.
function nearest(t, b, plates, bareU = null) {
  // Plates on, so collars on: a side of nothing is the bare bar, which is a
  // candidate of its own — 22.5 is nearer the empty bar than 25.5 of collars and
  // quarter plates, and a warm-up that lands on the bar is then dropped.
  const makes = (s) => s > 0 && stacks(plates, s, s, { cap: 1 }).length > 0
  // At or under bar-and-collars is the empty bar: a step that rounds to 25 is
  // the bar, not collars with nothing between them.
  if (bareU != null && t <= b) return [bareU]
  const side = (t - b) / 2
  if (Number.isInteger(side) && side >= 0 && makes(side)) return [t]
  const out = []
  const byGap = reachable(plates).map((s) => ({ s, d: Math.abs(b + 2 * s - t) })).sort((x, y) => x.d - y.d)
  for (const { s, d } of byGap) {
    if (out.length && d > out[0].d) break
    if (makes(s)) out.push({ s, d })
  }
  const totals = out.map((x) => b + 2 * x.s)
  if (bareU == null) return totals
  const d = Math.abs(bareU - t)
  if (!out.length || d < out[0].d) return [bareU]
  return d === out[0].d ? [bareU, ...totals] : totals
}

const shared = (a, b) => {
  let n = 0
  while (n < a.length && n < b.length && a[n] === b[n]) n++
  return n
}

// The heaviest per-side sum a stack can actually build, at or under `side`, whose
// total clears `floor`. Null when nothing does.
function under(side, floor, b, plates) {
  for (const s of reachable(plates).filter((x) => x <= side).reverse()) {
    if (s <= 0 || b + 2 * s <= floor) return null
    if (stacks(plates, s, s, { cap: 1 }).length) return s
  }
  return null
}

// Warm-ups up to the top set, in two separate decisions.
//
// WHICH TOTAL each set lands on is the lifter's arithmetic. A step is a
// percentage of the top set or of the last warm-up (`of: 'top' | 'last'`),
// rounded to the kit's increment (2.5kg, 5lb; ties go up, as his Shortcut does),
// then moved only if the plates owned can't make it, to the nearest total they
// can. Change plates go on where that total needs them.
//
// HOW it goes on is the plates': keep them on between sets — add 1, strip 3,
// each plate on the bar 0.25 — and the same cost picks between two totals
// equally near. It never moves a warm-up to a different total. That REPLACES the
// old rule, where fewer plate changes beat hitting each percentage: a lifter who
// works out 70% of 170 expects 120 on the bar, not whichever of 115 and 125
// saves a strip.
//
// `jump` is the one the lifter asked for today; every screen passes the same one.
export function buildRamp(lift, steps, kit, plates, jump = savedJump(lift, kit.unit)) {
  const top = loadBar(lift.topSet, kit, plates)
  if (!top.ok) return { ok: false, top }
  const b = base(kit)
  const topU = toU(lift.topSet)
  const inc = toU(jumpRule(kit.unit).step)
  const ordered = [...steps].sort((x, y) => x.position - y.position)
  // The first set: the bare bar when the start is at or under bar-and-collars —
  // collars go on with plates, so there is nothing between 20 and 25 to load.
  const bareU = bare(kit)
  const startU = toU(lift.startWeight ?? kit.barWeight)
  const start = startU <= b ? bareU : Math.min(...nearest(startU, b, plates))
  const startReps = lift.startReps ?? 5

  // THE LAST WARM-UP is placed first, because it bounds everything under it and
  // most steps are a share of it. It is exactly top minus the jump, and may use
  // change plates: they go on for the top set anyway. When that total can't be
  // made it takes the nearest one BELOW — a last warm-up heavier than asked for
  // is the one miss that costs the top set. A scheme with no warm-ups gets none,
  // and neither does a jump with no room above the start.
  const gap = toU(jump)
  const side = ordered.length ? under(Math.floor((topU - gap - b) / 2), start, b, plates) : null
  const last = side == null ? null : b + 2 * side

  // Each set as the totals it may land on. A step at or past the last warm-up
  // goes here; one at or under the set before it goes in the walk below, so a
  // light top set collapses to fewer sets rather than repeating a weight.
  const plan = []
  if (last != null || start < topU) {
    plan.push({ totals: [start], reps: startReps, repsTo: rangeTo(startReps, lift.startRepsTo), step: null })
  }
  if (last != null) {
    ordered.forEach((s, i) => {
      const pct = Number(s.percent)
      if (!(pct > 0)) return
      const from = s.of === 'last' ? last : topU
      const t = Math.round((from * pct) / 100 / inc + 1e-9) * inc
      const totals = nearest(t, b, plates, bareU).filter((x) => x < last)
      const reps = Number(s.reps) || 1
      if (totals.length) plan.push({ totals, reps, repsTo: rangeTo(reps, s.repsTo), step: i })
    })
    plan.push({ totals: [last], reps: 1, repsTo: null, step: null, lastWarmUp: true })
  }
  plan.push({ totals: [topU], reps: lift.topSetReps ?? 1, repsTo: null, step: null })

  const layers = plan
    .map(({ totals, ...set }) => totals.flatMap((total) => loadings(total, b, plates, bareU).map((stack) => ({ ...set, stack, total }))))
    .filter((l) => l.length)

  const cost = (prev, cur) => {
    if (prev && cur.total <= prev.total) return Infinity
    const p = prev ? prev.stack : []
    const n = shared(p, cur.stack)
    return cur.stack.length - n + 3 * (p.length - n) + 0.25 * cur.stack.length
  }
  let best = layers[0].map((c) => ({ score: cost(null, c), path: [c] }))
  for (const l of layers.slice(1)) {
    const next = l
      .map((c) => {
        let pick = { score: Infinity, path: [] }
        for (const p of best) {
          const s = p.score + cost(p.path[p.path.length - 1], c)
          if (s < pick.score) pick = { score: s, path: p.path }
        }
        return { score: pick.score, path: [...pick.path, c] }
      })
      .filter((x) => x.score < Infinity)
    if (next.length) best = next // a warm-up that can't clear the set before it is dropped
  }
  const path = best.reduce((a, x) => (x.score < a.score ? x : a)).path

  let prev = []
  const sets = path.map((c, i) => {
    const n = shared(prev, c.stack)
    const set = {
      total: fromU(c.total),
      reps: c.reps,
      repsTo: c.repsTo,
      step: c.step, // which step of the scheme this set is, or null for the start, last warm-up and top
      isTop: i === path.length - 1,
      isLastWarmUp: !!c.lastWarmUp,
      stack: c.stack.map(fromU),
      added: c.stack.slice(n).map(fromU),
      removed: prev.slice(n).map(fromU),
    }
    prev = c.stack
    return set
  })
  return { ok: true, sets }
}

export function delta({ stack, added = [], removed = [] }) {
  const list = (xs) => [...new Set(xs)].map((w) => { const n = xs.filter((x) => x === w).length; return n > 1 ? `${fmt(w)} ×${n}` : fmt(w) }).join(', ')
  if (!removed.length && added.length === stack.length) return stack.length ? `Load ${list(added)}.` : 'Empty bar.'
  return [removed.length && `Take off ${list(removed)}.`, added.length && `Add ${list(added)}.`].filter(Boolean).join(' ')
}

export const describe = (set) =>
  set.stack.length ? `Per side, inside out: ${set.stack.map(fmt).join(', ')}. ${delta(set)}` : 'Empty bar.'
